import React, { useEffect, useRef, useState } from 'react';

/*
  Lazy loader for @lordicon/react.

  @lordicon/react pulls in lottie-web, which is ~75kB gzipped - about a third of
  the whole JS bundle. These animated icons only ever appear well below the hero
  fold, so there is no reason to ship lottie in the main bundle. This component
  waits until the icon is about to scroll into view, then dynamically imports the
  Player (a single shared async chunk for every icon on the page) and plays it.

  The icon slots have fixed sizes in CSS, so mounting the Player later causes no
  layout shift.
*/

let playerPromise;
const loadPlayer = () => {
  if (!playerPromise) {
    playerPromise = import('@lordicon/react').then((m) => m.Player);
  }
  return playerPromise;
};

const LordIconLazy = ({ data, className, colorize = '#5d6b5e', size = '100%' }) => {
  const [Player, setPlayer] = useState(null);
  const playerRef = useRef(null);
  const wrapRef = useRef(null);

  // Load lottie when the icon nears the viewport - with a guaranteed fallback
  // (idle/timeout) so the icons always render even if IntersectionObserver
  // never fires (background tabs, unsupported edge cases).
  useEffect(() => {
    const el = wrapRef.current;
    let cancelled = false;
    let observer;
    let idleId;
    let timerId;

    const trigger = () => {
      if (cancelled) return;
      cancelled = true;
      if (observer) observer.disconnect();
      if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timerId) clearTimeout(timerId);
      loadPlayer().then((P) => {
        setPlayer(() => P);
      });
    };

    if (el && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) trigger();
        },
        { rootMargin: '250px' }
      );
      observer.observe(el);
    }

    // Fallback: load once the browser is idle after first paint, so the icons
    // never depend solely on a scroll/intersection event.
    if (window.requestIdleCallback) {
      idleId = window.requestIdleCallback(trigger, { timeout: 3000 });
    } else {
      timerId = setTimeout(trigger, 2000);
    }

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timerId) clearTimeout(timerId);
    };
  }, []);

  const handleComplete = () => {
    setTimeout(() => playerRef.current?.playFromBeginning(), 2500);
  };

  // Play once the Player has actually mounted.
  useEffect(() => {
    if (Player) playerRef.current?.playFromBeginning();
  }, [Player]);

  const fillStyle = className
    ? undefined
    : { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' };

  return (
    <div ref={wrapRef} className={className} style={fillStyle}>
      {Player && (
        <Player
          icon={data}
          ref={playerRef}
          size={size}
          onComplete={handleComplete}
          colorize={colorize}
        />
      )}
    </div>
  );
};

export default LordIconLazy;
